import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';

import { FEATURE_SELECTION_OCCLUDING_LAYER_IDS, ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import type { MapSelection } from '@/hooks/useSelectionStats';

/**
 * Click-to-select for Area of Interest and DMA polygons, driving the floating stats
 * card. Selection (and its visual highlight) lives in MapView; this hook only detects
 * the clicks and reports them via `onSelect`.
 *
 * Deliberately separate from `useDmaInteractions` (which owns the purely-visual hover
 * highlight): hover and selection are independent concerns. Empty-map clearing is NOT
 * this hook's job — the single global click listener (useMapClickDismissal) owns that
 * decision, so a click that lands on a polygon simply selects and a click that lands
 * nowhere is cleared exactly once, with no ordering dependency between the two.
 *
 * A click whose visible target is a marker, cluster bubble, count label, pipeline or
 * fitting rendered above the fill must not open the stats card — the fill layer's click
 * handler fires underneath regardless, so selection explicitly stands down when any
 * occluding layer reports a feature at the click point.
 *
 * AOI fill (Network Overview level) and DMA fill (DMA level inward) occupy different
 * semantic-zoom bands and are never both present, so the two layer handlers never
 * compete. Delegated layer listeners are safe to register before the layers exist and
 * re-resolve their layer ids per event, so they are registered once and survive a
 * base-map switch without re-attachment.
 */
export function useFeatureSelection(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    onSelect: (selection: MapSelection) => void,
    enabled: boolean = true,
): void {
    const onSelectRef = useRef(onSelect);
    useEffect(() => {
        onSelectRef.current = onSelect;
    });

    useEffect(() => {
        if (!enabled || !isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        // True when a marker/other interactive feature sits under the click point, so the
        // AOI/DMA fill click that also fired should be ignored (the card must not appear).
        const isOccludedByMarker = (point: maplibregl.PointLike): boolean => {
            const layers = FEATURE_SELECTION_OCCLUDING_LAYER_IDS.filter((id) => mlMap.getLayer(id));
            if (!layers.length) return false;
            return mlMap.queryRenderedFeatures(point, { layers }).length > 0;
        };

        const handleDmaClick = (e: maplibregl.MapLayerMouseEvent) => {
            if (isOccludedByMarker(e.point)) return;
            const props = e.features?.[0]?.properties as { ident?: string; name?: string } | undefined;
            if (props?.ident == null) return;
            onSelectRef.current({
                type: 'dma',
                ident: props.ident,
                name: props.name ?? props.ident,
                lngLat: [e.lngLat.lng, e.lngLat.lat],
            });
        };

        const handleAreaClick = (e: maplibregl.MapLayerMouseEvent) => {
            if (isOccludedByMarker(e.point)) return;
            const props = e.features?.[0]?.properties as { name?: string } | undefined;
            if (props?.name == null) return;
            onSelectRef.current({
                type: 'aoi',
                name: props.name,
                lngLat: [e.lngLat.lng, e.lngLat.lat],
            });
        };

        const setPointer = () => {
            mlMap.getCanvas().style.cursor = 'pointer';
        };
        const clearPointer = () => {
            mlMap.getCanvas().style.cursor = '';
        };

        // Registered once — delegated layer listeners re-resolve their layer ids on every
        // event, so they survive a style reload. Re-registering them on `style.load` would
        // duplicate them: MapLibre dedupes plain listeners but not delegated ones.
        mlMap.on('click', ORBIT_LAYER_IDS.dmaFill, handleDmaClick);
        mlMap.on('click', ORBIT_LAYER_IDS.surveyedAreasFill, handleAreaClick);
        mlMap.on('mouseenter', ORBIT_LAYER_IDS.dmaFill, setPointer);
        mlMap.on('mouseleave', ORBIT_LAYER_IDS.dmaFill, clearPointer);
        mlMap.on('mouseenter', ORBIT_LAYER_IDS.surveyedAreasFill, setPointer);
        mlMap.on('mouseleave', ORBIT_LAYER_IDS.surveyedAreasFill, clearPointer);

        return () => {
            mlMap.off('click', ORBIT_LAYER_IDS.dmaFill, handleDmaClick);
            mlMap.off('click', ORBIT_LAYER_IDS.surveyedAreasFill, handleAreaClick);
            mlMap.off('mouseenter', ORBIT_LAYER_IDS.dmaFill, setPointer);
            mlMap.off('mouseleave', ORBIT_LAYER_IDS.dmaFill, clearPointer);
            mlMap.off('mouseenter', ORBIT_LAYER_IDS.surveyedAreasFill, setPointer);
            mlMap.off('mouseleave', ORBIT_LAYER_IDS.surveyedAreasFill, clearPointer);
            mlMap.getCanvas().style.cursor = '';
        };
    }, [enabled, isMapLoaded, mapRef]);
}
